import { useLocalStorage } from '@shared/utils/useStorage';
import type {
  ExpandedState,
  FilterFn,
  Row,
  RowData,
  RowSelectionState,
  TableOptions,
  TableState,
} from '@tanstack/react-table';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { StateCleanup } from './components/StateCleanup';
import { Context } from './DataProviderContext';
import { DefaultColumnFilter } from './filter/DefaultColumnFilter';
import { FilterProvider } from './filter/FilterContext';
import { useTableState } from './hooks/useTableState';
import type { TableColumnDef } from './internal/getTableColumnDef';
import type { ExtendedColumnDef, TableAction } from './types';
import { useExtendedColumnDefs } from './useExtendedColumnDefs';

type StructuredRowData = RowData & {
  subRows?: StructuredRowData[];
};

export interface BaseDataProviderProps<TData extends RowData, TTexts = never> {
  children?: ReactNode;
  columnDefs: ExtendedColumnDef<TData, TTexts>[];
  initialData: any[];
  initialState?: Partial<TableState>;
  actions?: TableAction[];
  isLoading?: boolean;
  isRowIndeterminate?: (row: Row<TData>) => boolean;
  isRowHighlighted?: (row: Row<TData>) => boolean;
  isRowClickable?: (row: Row<TData>) => boolean;
  onRowClick?: (row: Row<TData>) => void;
  toolbarControls?: ReactNode;
  id?: string;
}

export interface ClientSideDataProviderProps<
  TData extends RowData,
  TTexts = never,
> extends BaseDataProviderProps<TData, TTexts> {
  enableFilters?: boolean;
  enableGlobalFiltering?: boolean;
  globalFilterFn?: FilterFn<TData>;
  enableGrouping?: boolean;
  enableSorting?: boolean;
  enablePagination?: boolean;
  enableStructure?: boolean;
  toStructure?: (flatData: TData[]) => StructuredRowData[];
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void;
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
}

export const DataProvider = <TData extends RowData, TTexts = never>({
  children,
  columnDefs,
  initialData,
  initialState,
  actions = [],
  enableFilters = false,
  enableGlobalFiltering = false,
  globalFilterFn,
  enableGrouping = false,
  enableSorting = false,
  enablePagination = false,
  enableStructure = false,
  toStructure,
  isLoading = false,
  isRowIndeterminate,
  isRowClickable,
  isRowHighlighted,
  onRowClick,
  toolbarControls,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  id,
}: ClientSideDataProviderProps<TData, TTexts>) => {
  const extendedColumnDefs = useExtendedColumnDefs<TData, TTexts>(columnDefs);
  const { tableState, onStateChange } = useTableState(id, initialState);
  const [expanded, setExpanded] = useLocalStorage<ExpandedState>(
    [id, 'expanded'].join('.'),
    {}
  );
  const data = useMemo(
    () =>
      enableStructure && typeof toStructure === 'function'
        ? toStructure(initialData)
        : initialData,
    [enableStructure, toStructure, initialData]
  );
  const state: Partial<TableState> = {
    ...tableState,
  };
  const defaultColumn: Partial<TableColumnDef<TData, TTexts>> = {
    Filter: DefaultColumnFilter,
  };

  const options: TableOptions<TData> = {
    columns: extendedColumnDefs,
    data,
    state,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    enableFilters: false,
    enableGrouping: false,
    enableRowSelection: false,
    defaultColumn,
    onStateChange,
  };

  if (enableSorting) {
    options.enableSorting = true;
    options.getSortedRowModel = getSortedRowModel();
  }

  if (enableFilters) {
    options.enableFilters = true;
    options.filterFromLeafRows = false;
    options.getFilteredRowModel = getFilteredRowModel();
    options.getFacetedRowModel = getFacetedRowModel();
    options.getFacetedUniqueValues = getFacetedUniqueValues();
    options.getFacetedMinMaxValues = getFacetedMinMaxValues();
  }

  if (enableGlobalFiltering) {
    options.enableGlobalFilter = true;
    options.getFilteredRowModel = getFilteredRowModel();
    if (globalFilterFn) {
      options.globalFilterFn = globalFilterFn;
    }
  }

  if (enableGrouping) {
    options.enableGrouping = true;
    options.getGroupedRowModel = getGroupedRowModel();
    options.getExpandedRowModel = getExpandedRowModel();
  }

  if (enablePagination) {
    options.getPaginationRowModel = getPaginationRowModel();
  }

  if (enableStructure) {
    state.expanded = expanded;
    options.onExpandedChange = (updater) => {
      const next = typeof updater === 'function' ? updater(expanded) : updater;
      setExpanded(next);
    };
    options.getSubRows = ((row: StructuredRowData) =>
      row.subRows) as TableOptions<TData>['getSubRows'];
    options.getExpandedRowModel = getExpandedRowModel();
    options.filterFromLeafRows = true;
  }

  if (rowSelection && onRowSelectionChange) {
    options.enableRowSelection = true;
    state.rowSelection = rowSelection;
    options.onRowSelectionChange = (updater) => {
      const next =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      onRowSelectionChange(next);
    };
    if (getRowId) {
      options.getRowId = getRowId;
    }
  }

  const table = useReactTable(options);

  return (
    <Context.Provider
      value={{
        table,
        actions,
        toolbarControls,
        isLoading,
        isRowIndeterminate,
        isRowClickable,
        isRowHighlighted,
        onRowClick,
        getRowCount: () => table.getExpandedRowModel().rows.length,
        id,
      }}
    >
      <FilterProvider>
        <StateCleanup />
        {children}
      </FilterProvider>
    </Context.Provider>
  );
};
